import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { TwoFactorSetupCodeResponse } from '../../../model/account/twoFactorAuthentication/twoFactorSetupCodeResponse';
import { twoFactorAuthSetupCompletedAndEnabled } from '../../../redux/actions/accountActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { selectAccount } from '../../../redux/selectors/accountSelectors';
import accountService from '../../../services/AccountService';

const EnableTwoFactorAuthComponent: React.FC = () => {
  const { t } = useTranslation('auth');
  const account = useSelector(selectAccount);
  const dispatch = useAppThunkDispatch();
  const [setupInfo, setSetupInfo] = useState<TwoFactorSetupCodeResponse | null>(
    null
  );
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  useEffect(() => {
    if (
      account.isTwoFactorAuthenticationEnabled &&
      !account.isTwoFactorAuthenticationSetupCompleted
    ) {
      accountService
        .enableTwoFactor(account.accountGuid)
        .then((response: TwoFactorSetupCodeResponse) => {
          setSetupInfo(response);
        })
        .catch((error: any) => {
          //dispatch(loginUserError(error.body.message));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  return (
    <>
      <Typography variant="h4">
        <strong>{t('twoFactor.title')}</strong>
      </Typography>
      <Box
        sx={{
          mt: 2,
          mb: 1,
        }}
      >
        <>
          <Typography>{t('twoFactor.enable.intro')}</Typography>
          <Typography>{t('twoFactor.enable.stepsIntro')}</Typography>
          <Box sx={{ m: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Typography variant="h5">1.</Typography>
              <Typography
                sx={{
                  mt: '7px',
                  ml: 1,
                }}
              >
                {t('twoFactor.enable.step1')}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Typography variant="h5">2.</Typography>
              <Typography
                sx={{
                  mt: '7px',
                  ml: 1,
                }}
              >
                {t('twoFactor.enable.step2')}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Typography variant="h5">3.</Typography>
              <Typography
                sx={{
                  mt: '7px',
                  ml: 1,
                }}
              >
                {t('twoFactor.enable.step3')}{' '}
                <Link
                  sx={{ cursor: 'pointer' }}
                  color="inherit"
                  onClick={() =>
                    dispatch(twoFactorAuthSetupCompletedAndEnabled())
                  }
                >
                  {t('twoFactor.enable.step3Link')}
                </Link>
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              mt: 1,
              mb: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              component="img"
              sx={{
                height: 250,
                width: 250,
                maxHeight: { xs: 250, md: 250 },
                maxWidth: { xs: 250, md: 250 },
              }}
              alt="qr code"
              src={setupInfo?.qrCodeSetupImageUrl}
            />
          </Box>
          <Accordion
            expanded={expanded === 'panel1'}
            onChange={handleChange('panel1')}
          >
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
            >
              <Typography variant="h6">
                {t('twoFactor.enable.faq.whatTitle')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  rowGap: 1,
                }}
              >
                <Typography>{t('twoFactor.enable.faq.what1')}</Typography>
                <Typography>{t('twoFactor.enable.faq.what2')}</Typography>
                <Typography>{t('twoFactor.enable.faq.what3')}</Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'panel2'}
            onChange={handleChange('panel2')}
          >
            <AccordionSummary
              aria-controls="panel2d-content"
              id="panel2d-header"
            >
              <Typography variant="h6">
                {' '}
                {t('twoFactor.enable.faq.cantScanTitle')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <>
                <Typography>
                  <Trans
                    t={t}
                    i18nKey="twoFactor.enable.faq.cantScan1"
                    components={{ strong: <strong /> }}
                  />
                </Typography>
                <Typography>
                  <Trans
                    t={t}
                    i18nKey="twoFactor.enable.faq.cantScan2"
                    components={{ strong: <strong /> }}
                  />
                </Typography>
                <Typography>{t('twoFactor.enable.faq.cantScan3')}</Typography>
                <Box
                  sx={{
                    border: 1,
                    mt: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{ margin: 10, wordWrap: 'break-word' }}
                  >
                    {setupInfo?.manualEntryKey}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    mt: 1,
                  }}
                >
                  <Trans
                    t={t}
                    i18nKey="twoFactor.enable.faq.cantScan4"
                    components={{ strong: <strong /> }}
                  />
                </Typography>
              </>
            </AccordionDetails>
          </Accordion>
        </>
      </Box>
      <Link href="/" variant="body2" sx={{ mt: 1 }}>
        {t('twoFactor.returnHome')}
      </Link>
    </>
  );
};

export default EnableTwoFactorAuthComponent;
